import { ChevronRightIcon } from "lucide-react";
import { NavLink as RouterNavLink, useLocation } from "react-router";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import type { NavEntry } from "@/components/layout/nav-items";

/**
 * Renders a role's nav entries against the real Sidebar primitive —
 * ported from the reference's `nav-main.tsx`, trimmed to what GAS
 * actually uses: no "Quick Create" demo button, no badges, no submenu
 * nesting beyond one level. Kept: the collapsed-desktop dropdown fallback
 * for a group with children, which the app's earlier hand-rolled nav list
 * didn't have — collapsing the sidebar used to just flatten the "Reports"
 * group's children in place; now it becomes a proper dropdown, like the
 * top-level items.
 */
export function NavMain({ items }: { items: NavEntry[] }) {
  const location = useLocation();

  const isActive = (href: string) => location.pathname === href || location.pathname.startsWith(`${href}/`);
  const isGroupActive = (group: Extract<NavEntry, { kind: "group" }>) =>
    group.children.some((child) => isActive(child.href));

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) =>
            item.kind === "link" ? (
              <NavLinkItem key={item.href} item={item} active={isActive(item.href)} />
            ) : (
              <NavGroupItem key={item.label} item={item} active={isGroupActive(item)} isActive={isActive} />
            ),
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

function NavLinkItem({
  item,
  active,
}: {
  item: Extract<NavEntry, { kind: "link" }>;
  active: boolean;
}) {
  const Icon = item.icon;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton tooltip={item.label} isActive={active} render={<RouterNavLink to={item.href} />}>
        <Icon />
        <span>{item.label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function NavGroupItem({
  item,
  active,
  isActive,
}: {
  item: Extract<NavEntry, { kind: "group" }>;
  active: boolean;
  isActive: (href: string) => boolean;
}) {
  const { state, isMobile } = useSidebar();
  const Icon = item.icon;
  const collapsedDesktop = state === "collapsed" && !isMobile;

  if (collapsedDesktop) {
    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton tooltip={item.label} isActive={active}>
                <Icon />
                <span>{item.label}</span>
              </SidebarMenuButton>
            }
          />
          <DropdownMenuContent side="right" align="start" sideOffset={12} className="w-48">
            {item.children.map((child) => (
              <DropdownMenuItem key={child.href} render={<RouterNavLink to={child.href} />}>
                {child.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible defaultOpen={active} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger
          render={
            <SidebarMenuButton tooltip={item.label} isActive={active}>
              <Icon />
              <span>{item.label}</span>
              <ChevronRightIcon className="ml-auto transition-transform duration-200 group-data-[open]/collapsible:rotate-90" />
            </SidebarMenuButton>
          }
        />
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.children.map((child) => (
              <SidebarMenuSubItem key={child.href}>
                <SidebarMenuSubButton isActive={isActive(child.href)} render={<RouterNavLink to={child.href} />}>
                  {child.label}
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}
